/**
 * Engine edit dialog — sysadmin Models → brand detail → Engine pool.
 *
 * Makes a brand's engine dispatch config editable (config-as-data): context
 * window, sampling params, baseline system prompt, guardrail profile,
 * provenance eligibility, role, default flag. Backed by
 * PATCH /admin/platform/argus-models/:id/engines/:engineId.
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useUpdateModelEngine } from '@/lib/use-argus-models';

export interface EngineLike {
  id: string;
  roleLabel: string;
  isDefault: boolean;
  provenanceEligibility: string[] | null;
  defaultTemperature: string | number | null;
  defaultTopP: string | number | null;
  defaultTopK: number | null;
  defaultMaxTokens: number | null;
  defaultRepeatPenalty: string | number | null;
  defaultCtxSize: number;
  systemPrompt: string;
  guardrailProfile: string;
  baseLlmModelKey?: string;
}

const ELIGIBILITY = ['us-only', 'us-eu', 'unrestricted'] as const;

export function EngineEditDialog({
  brandId,
  engine,
  open,
  onOpenChange,
}: {
  brandId: string;
  engine: EngineLike | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const mut = useUpdateModelEngine(brandId);

  const [ctx, setCtx] = useState('');
  const [temp, setTemp] = useState('');
  const [topP, setTopP] = useState('');
  const [topK, setTopK] = useState('');
  const [maxTokens, setMaxTokens] = useState('');
  const [repeat, setRepeat] = useState('');
  const [guardrail, setGuardrail] = useState('baseline');
  const [role, setRole] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [elig, setElig] = useState<string[]>([]);
  const [prompt, setPrompt] = useState('');

  useEffect(() => {
    if (open && engine) {
      setCtx(String(engine.defaultCtxSize ?? ''));
      setTemp(engine.defaultTemperature == null ? '' : String(engine.defaultTemperature));
      setTopP(engine.defaultTopP == null ? '' : String(engine.defaultTopP));
      setTopK(engine.defaultTopK == null ? '' : String(engine.defaultTopK));
      setMaxTokens(engine.defaultMaxTokens == null ? '' : String(engine.defaultMaxTokens));
      setRepeat(engine.defaultRepeatPenalty == null ? '' : String(engine.defaultRepeatPenalty));
      setGuardrail(engine.guardrailProfile || 'baseline');
      setRole(engine.roleLabel || '');
      setIsDefault(!!engine.isDefault);
      setElig(engine.provenanceEligibility ?? []);
      setPrompt(engine.systemPrompt || '');
    }
  }, [open, engine?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleElig(v: string) {
    setElig((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  }

  async function save() {
    if (!engine) return;
    const ctxN = parseInt(ctx, 10);
    if (!Number.isInteger(ctxN) || ctxN < 1024) {
      toast.error('Context size must be a whole number ≥ 1024');
      return;
    }
    if (elig.length === 0) {
      toast.error('Pick at least one provenance eligibility level');
      return;
    }
    const body: Record<string, unknown> = {
      roleLabel: role.trim() || undefined,
      isDefault,
      provenanceEligibility: elig,
      defaultTemperature: temp === '' ? undefined : Number(temp),
      defaultTopP: topP === '' ? undefined : Number(topP),
      defaultTopK: topK === '' ? null : parseInt(topK, 10),
      defaultMaxTokens: maxTokens === '' ? null : parseInt(maxTokens, 10),
      defaultRepeatPenalty: repeat === '' ? null : Number(repeat),
      defaultCtxSize: ctxN,
      systemPrompt: prompt,
      guardrailProfile: guardrail,
    };
    try {
      await mut.mutateAsync({ engineId: engine.id, body });
      toast.success('Engine updated');
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update engine');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit engine</DialogTitle>
          <DialogDescription className="font-mono text-xs">
            {engine?.baseLlmModelKey ?? engine?.id}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium">Role label</label>
              <Input value={role} onChange={(e) => setRole(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Default engine</label>
              <Select value={isDefault ? 'yes' : 'no'} onValueChange={(v) => setIsDefault(v === 'yes')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes — default for this brand</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium">Context window</label>
              <Input value={ctx} onChange={(e) => setCtx(e.target.value)} inputMode="numeric" />
              <p className="text-[10px] text-muted-foreground">tokens · must not exceed what the inference server serves</p>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Max tokens (out)</label>
              <Input value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} inputMode="numeric" placeholder="(unset)" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Guardrail profile</label>
              <Select value={guardrail} onValueChange={setGuardrail}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baseline">baseline</SelectItem>
                  <SelectItem value="standard">standard</SelectItem>
                  <SelectItem value="maximum">maximum</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium">Temperature</label>
              <Input value={temp} onChange={(e) => setTemp(e.target.value)} inputMode="decimal" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Top P</label>
              <Input value={topP} onChange={(e) => setTopP(e.target.value)} inputMode="decimal" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Top K</label>
              <Input value={topK} onChange={(e) => setTopK(e.target.value)} inputMode="numeric" placeholder="(unset)" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Repeat penalty</label>
              <Input value={repeat} onChange={(e) => setRepeat(e.target.value)} inputMode="decimal" placeholder="(unset)" />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium">Provenance eligibility</label>
            <div className="flex items-center gap-4">
              {ELIGIBILITY.map((v) => (
                <label key={v} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <Checkbox checked={elig.includes(v)} onCheckedChange={() => toggleElig(v)} />
                  {v}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium">Baseline system prompt</label>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} className="font-mono text-xs" placeholder="(none)" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Save engine'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
